import React, { useState, useMemo, useEffect } from 'react';
import { getTenants, updateTenant, subscribeToTenants, unsubscribeChannel, logActivity } from '../../services/supabaseService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import TenantMetrics from './components/TenantMetrics';
import TenantFilters from './components/TenantFilters';
import BulkActions from './components/BulkActions';
import TenantTable from './components/TenantTable';
import TenantCard from './components/TenantCard';
import TenantEditModal from './components/TenantEditModal';
import RiskLevelModal from './components/RiskLevelModal';

const TenantManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [expirationFilter, setExpirationFilter] = useState('all');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [editingTenant, setEditingTenant] = useState(null);
  const [riskLevelTenant, setRiskLevelTenant] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);

  useEffect(() => {
    loadTenants();

    // Subscribe to real-time tenants changes
    const channel = subscribeToTenants(() => {
      loadTenants();
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribeChannel(channel);
    };
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTenants();
      setTenants(data || []);
    } catch (err) {
      console.error('Tenants load error:', err);
      setError(err?.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = useMemo(() => {
    return tenants?.filter(tenant => {
      const matchesSearch = !searchTerm || 
        tenant?.companyName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        tenant?.contactPerson?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        tenant?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesRisk = riskFilter === 'all' || tenant?.riskLevel === riskFilter;

      let matchesExpiration = true;
      if (expirationFilter !== 'all') {
        const today = new Date();
        const leaseEnd = new Date(tenant.leaseEndDate);
        const diffTime = leaseEnd - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        switch (expirationFilter) {
          case 'expiring_30':
            matchesExpiration = diffDays <= 30 && diffDays > 0;
            break;
          case 'expiring_60':
            matchesExpiration = diffDays <= 60 && diffDays > 0;
            break;
          case 'expiring_90':
            matchesExpiration = diffDays <= 90 && diffDays > 0;
            break;
          case 'expired':
            matchesExpiration = diffDays <= 0;
            break;
          default:
            matchesExpiration = true;
        }
      }

      return matchesSearch && matchesRisk && matchesExpiration;
    });
  }, [tenants, searchTerm, riskFilter, expirationFilter]);

  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev => 
      prev?.includes(tenantId) 
        ? prev?.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTenants?.length === filteredTenants?.length) {
      setSelectedTenants([]);
    } else {
      setSelectedTenants(filteredTenants?.map(tenant => tenant?.id));
    }
  };

  const handleEditTenant = async (tenantId, updates) => {
    try {
      const tenant = tenants?.find(t => t?.id === tenantId);
      
      await updateTenant(tenantId, updates);
      await loadTenants();
      setIsEditModalOpen(false);
      setEditingTenant(null);
      
      // Log activity
      await logActivity({
        module: 'tenants',
        action: 'update',
        entityType: 'tenant',
        entityId: tenantId,
        entityName: tenant?.companyName || 'Tenant',
        description: 'Updated tenant information',
        newValue: updates
      });
    } catch (err) {
      console.error('Edit tenant error:', err);
      alert('Failed to update tenant');
    }
  };

  const handleSetRiskLevel = (tenant) => {
    setRiskLevelTenant(tenant);
    setIsRiskModalOpen(true);
  };

  const handleUpdateRiskLevel = async (tenantId, newRiskLevel) => {
    try {
      const tenant = tenants?.find(t => t?.id === tenantId);
      const oldRiskLevel = tenant?.riskLevel;
      
      await updateTenant(tenantId, { riskLevel: newRiskLevel });
      await loadTenants();
      setIsRiskModalOpen(false);
      setRiskLevelTenant(null);
      
      // Log activity
      await logActivity({
        module: 'tenants',
        action: 'update',
        entityType: 'tenant',
        entityId: tenantId,
        entityName: tenant?.companyName || 'Tenant',
        description: `Updated risk level from ${oldRiskLevel} to ${newRiskLevel}`,
        oldValue: { riskLevel: oldRiskLevel },
        newValue: { riskLevel: newRiskLevel }
      });
    } catch (err) {
      console.error('Update risk level error:', err);
      alert('Failed to update risk level');
    }
  };

  const handleSaveTenant = async (updatedTenant) => {
    try {
      await updateTenant(updatedTenant?.id, updatedTenant);
      await loadTenants();
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Tenant update error:', err);
      alert('Failed to update tenant');
    }
  };

  const handleSaveRiskLevel = async (updatedTenant) => {
    try {
      await updateTenant(updatedTenant?.id, { riskLevel: updatedTenant?.riskLevel });
      await loadTenants();
      setIsRiskModalOpen(false);
    } catch (err) {
      console.error('Risk level update error:', err);
      alert('Failed to update risk level');
    }
  };

  const handleBulkRiskUpdate = async (tenantIds, riskLevel) => {
    try {
      await Promise.all(
        tenantIds?.map(id => updateTenant(id, { riskLevel }))
      );
      await loadTenants();
      setSelectedTenants([]);
    } catch (err) {
      console.error('Bulk risk update error:', err);
      alert('Failed to update risk levels');
    }
  };

  const handleBulkExport = (tenantIds) => {
    const selectedTenantsData = tenants?.filter(tenant => tenantIds?.includes(tenant?.id));
    console.log('Exporting tenants:', selectedTenantsData);
    alert(`Exporting ${selectedTenantsData?.length} tenants to CSV`);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRiskFilter('all');
    setExpirationFilter('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading tenants...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
        <p className="text-danger-800">Error: {error}</p>
        <button
          onClick={loadTenants}
          className="mt-2 text-sm text-danger-600 hover:text-danger-700 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">
          Tenant Management
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage tenant relationships, track revenue, and monitor risk levels across your coworking space.
        </p>
      </div>

      <TenantMetrics tenants={filteredTenants} />

      <TenantFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        riskFilter={riskFilter}
        onRiskFilterChange={setRiskFilter}
        expirationFilter={expirationFilter}
        onExpirationFilterChange={setExpirationFilter}
        onReset={handleClearFilters}
      />

      <BulkActions
        selectedCount={selectedTenants?.length}
        selectedTenants={selectedTenants}
        onBulkAction={handleBulkRiskUpdate}
        onBulkRiskUpdate={handleBulkRiskUpdate}
        onBulkExport={handleBulkExport}
        onClearSelection={() => setSelectedTenants([])}
      />

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Showing {filteredTenants?.length} of {tenants?.length} tenants
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
          iconName={viewMode === 'table' ? 'Grid3X3' : 'List'}
          iconPosition="left"
        >
          {viewMode === 'table' ? 'Card View' : 'Table View'}
        </Button>
      </div>

      {viewMode === 'table' ? (
        <TenantTable
          tenants={filteredTenants}
          onEdit={handleEditTenant}
          onSetRiskLevel={handleSetRiskLevel}
          selectedTenants={selectedTenants}
          onSelectTenant={handleSelectTenant}
          onSelectAll={handleSelectAll}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTenants?.map(tenant => (
            <TenantCard
              key={tenant?.id}
              tenant={tenant}
              onEdit={handleEditTenant}
              onSetRiskLevel={handleSetRiskLevel}
            />
          ))}
        </div>
      )}

      {filteredTenants?.length === 0 && (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No tenants found
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchTerm || riskFilter !== 'all' || expirationFilter !== 'all'
              ? "Try adjusting your search criteria or filters." : "Get started by adding your first tenant to the system."
            }
          </p>
          {(searchTerm || riskFilter !== 'all' || expirationFilter !== 'all') && (
            <Button
              variant="outline"
              onClick={handleClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear Filters
            </Button>
          )}
        </div>
      )}

      <TenantEditModal
        tenant={editingTenant}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingTenant(null);
        }}
        onSave={handleSaveTenant}
      />
      <RiskLevelModal
        tenant={riskLevelTenant}
        isOpen={isRiskModalOpen}
        onClose={() => {
          setIsRiskModalOpen(false);
          setRiskLevelTenant(null);
        }}
        onSave={handleSaveRiskLevel}
      />
    </div>
  );
};

export default TenantManagement;